import { Suspense } from 'react';
// components
import LoadingScreen from '../components/loading-screen';
import PublicPageTheme from '../components/public-page-theme';
import ProfileSkeleton from '../pages/dashboard/ProfileSkeleton';
import lazyWithReload from './lazyWithReload';

// ----------------------------------------------------------------------

const Loadable = (Component) => (props) =>
  (
    <Suspense fallback={<LoadingScreen />}>
      <Component {...props} />
    </Suspense>
  );

const LoadableContent = (Component) => (props) =>
  (
    <Suspense fallback={<LoadingScreen contained />}>
      <Component {...props} />
    </Suspense>
  );

// Rotas públicas já forçam tema claro na página (PublicPageTheme), mas o fallback do
// Suspense renderiza ANTES do chunk carregar — sem o tema aqui, o loading herda o
// themeMode global (dark por padrão) e a tela pisca escuro antes de virar branca.
const LoadablePublic = (Component) => (props) =>
  (
    <Suspense
      fallback={
        <PublicPageTheme>
          <LoadingScreen />
        </PublicPageTheme>
      }
    >
      <Component {...props} />
    </Suspense>
  );

// ----------------------------------------------------------------------

// AUTH
export const LoginPage = Loadable(lazyWithReload(() => import('../pages/auth/LoginPage')));
export const RegisterPage = Loadable(lazyWithReload(() => import('../pages/auth/RegisterPage')));
export const VerifyCodePage = Loadable(
  lazyWithReload(() => import('../pages/auth/VerifyCodePage'))
);
export const NewPasswordPage = Loadable(
  lazyWithReload(() => import('../pages/auth/NewPasswordPage'))
);
export const ResetPasswordPage = Loadable(
  lazyWithReload(() => import('../pages/auth/ResetPasswordPage'))
);
export const CreatePasswordPage = Loadable(
  lazyWithReload(() => import('../pages/auth/CreatePasswordPage'))
);

// DASHBOARD: GENERAL
export const DashboardHomePage = Loadable(
  lazyWithReload(() => import('../pages/dashboard/DashboardHomePage'))
);
export const DashboardV2 = Loadable(lazyWithReload(() => import('../pages/dashboard/DashboardV2')));
export const OperationHealthPage = Loadable(
  lazyWithReload(() => import('../pages/dashboard/OperationHealthPage'))
);
export const MySales = Loadable(lazyWithReload(() => import('../pages/dashboard/MySales')));
export const CaktoMcpPage = Loadable(lazyWithReload(() => import('../pages/dashboard/CaktoMcp')));
export const ComplianceMembersV3Page = Loadable(
  lazyWithReload(() => import('../pages/dashboard/ComplianceMembersV3'))
);
export const MySalesDisputes = Loadable(
  lazyWithReload(() => import('../pages/dashboard/MySalesDisputes'))
);
export const MySalesDisputeDetail = Loadable(
  lazyWithReload(() => import('../pages/dashboard/MySalesDisputeDetail'))
);
export const Subscriptions = Loadable(
  lazyWithReload(() => import('../pages/dashboard/Subscriptions'))
);
// export const Collaborators = Loadable(lazyWithReload(() => import('../pages/dashboard/Collaborators')));
export const BankingHome = Loadable(
  lazyWithReload(() => import('../pages/dashboard/Banking/Home'))
);
export const BankingCreditCards = Loadable(
  lazyWithReload(() => import('../pages/dashboard/Banking/CreditCards'))
);
export const BankingCreditCardTransactions = Loadable(
  lazyWithReload(() => import('../pages/dashboard/Banking/CreditCardTransactions'))
);
export const BankingCreditCardCreation = Loadable(
  lazyWithReload(() => import('../pages/dashboard/Banking/CreditCardCreation'))
);
export const BankingBoletoAssistant = Loadable(
  lazyWithReload(() => import('../pages/dashboard/Banking/BoletoAssistant'))
);
export const BankingBoletoEmit = Loadable(
  lazyWithReload(() => import('../pages/dashboard/Banking/BoletoEmit'))
);
export const BankingBoletoPayment = Loadable(
  lazyWithReload(() => import('../pages/dashboard/Banking/BoletoPayment'))
);
export const BankingTED = Loadable(lazyWithReload(() => import('../pages/dashboard/Banking/TED')));
export const BankingMobileRecharge = Loadable(
  lazyWithReload(() => import('../pages/dashboard/Banking/MobileRecharge'))
);
export const BankingGiftCard = Loadable(
  lazyWithReload(() => import('../pages/dashboard/Banking/GiftCard'))
);
export const Financial = Loadable(lazyWithReload(() => import('../pages/dashboard/Financial')));
export const FinancialCalendar = Loadable(
  lazyWithReload(() => import('../pages/dashboard/Financial/FinancialCalendar'))
);
export const FinancialCreditCard = Loadable(
  lazyWithReload(() => import('../pages/dashboard/Financial/FinancialCreditCard'))
);
export const FinancialCreditCardTransactions = Loadable(
  lazyWithReload(() => import('../pages/dashboard/Financial/FinancialCreditCardTransactions'))
);
export const FinancialPixKeys = Loadable(
  lazyWithReload(() => import('../pages/dashboard/Financial/FinancialPixKeys'))
);
export const FinancialCreateBankingAccount = Loadable(
  lazyWithReload(() => import('../pages/dashboard/Financial/CreateBankingAccountPage'))
);

export const FinancialInvoicesDashboard = Loadable(
  lazyWithReload(() => import('../pages/dashboard/Invoice/InvoicesDashboard'))
);

export const FinancialInvoicesAvulsa = Loadable(
  lazyWithReload(() => import('../pages/dashboard/Invoice/NotaAvulsa'))
);

export const FinancialInvoicesSettings = Loadable(
  lazyWithReload(() => import('../pages/dashboard/Invoice/InvoicesSettings'))
);

export const FinancialInvoicesRegister = Loadable(
  lazyWithReload(() => import('../pages/dashboard/Invoice/InvoicesRegister'))
);

export const FinancialInvoicesEdit = Loadable(
  lazyWithReload(() => import('../pages/dashboard/Invoice/InvoicesEdit'))
);

export const BankingAccount = Loadable(
  lazyWithReload(() => import('../pages/dashboard/Financial/BankingAccount'))
);
export const Apps = Loadable(lazyWithReload(() => import('../pages/dashboard/Apps')));
export const Webhooks = Loadable(lazyWithReload(() => import('../pages/dashboard/Webhooks')));
export const WebhookLogs = Loadable(lazyWithReload(() => import('../pages/dashboard/WebhookLogs')));
export const WebhookSettings = Loadable(
  lazyWithReload(() => import('../pages/dashboard/WebhookSettings'))
);
export const WebhooksRecentActivities = Loadable(
  lazyWithReload(() => import('@/pages/dashboard/WebhooksRecentActivities'))
);
export const Reports = Loadable(lazyWithReload(() => import('../pages/dashboard/reports/Reports')));
const ProfileLazy = lazyWithReload(() => import('../pages/dashboard/Profile'));
export const Profile = (props) => (
  <Suspense fallback={<ProfileSkeleton />}>
    <ProfileLazy {...props} />
  </Suspense>
);
export const AddFees = Loadable(lazyWithReload(() => import('../pages/dashboard/AddFees')));
export const ReportAbandonedSales = Loadable(
  lazyWithReload(() => import('../pages/dashboard/ReportAbandonedSales'))
);

export const ReportWhatsappRecovery = Loadable(
  lazyWithReload(() => import('../pages/dashboard/ReportWhatsappRecovery'))
);

export const ReportAffiliatesRevenue = Loadable(
  lazyWithReload(() => import('../pages/dashboard/reports/ReportAffiliatesRevenue'))
);
export const ReportCoProductionRevenue = Loadable(
  lazyWithReload(() => import('../pages/dashboard/reports/ReportCoProductionRevenue'))
);
export const ReportCoursesEngagement = Loadable(
  lazyWithReload(() => import('../pages/dashboard/ReportCoursesEngagement'))
);
export const ReportFinancialEstimate = Loadable(
  lazyWithReload(() => import('../pages/dashboard/reports/ReportFinancialEstimate'))
);
export const ReportProductsRevenue = Loadable(
  lazyWithReload(() => import('../pages/dashboard/reports/ReportProductsRevenue'))
);
export const Products = Loadable(lazyWithReload(() => import('../pages/dashboard/Products')));
export const AddProduct = Loadable(lazyWithReload(() => import('../pages/dashboard/AddProduct')));
export const AddProductV2 = Loadable(
  lazyWithReload(() => import('../pages/dashboard/AddProductV2'))
);
export const ShowcaseProducts = Loadable(
  lazyWithReload(() => import('../pages/dashboard/ShowcaseProducts'))
);
export const Courses = Loadable(lazyWithReload(() => import('../pages/course/Courses')));
export const StudentProfile = Loadable(
  lazyWithReload(() => import('../pages/student/StudentProfile'))
);
export const StudentNotifications = Loadable(
  lazyWithReload(() => import('../pages/student/StudentNotifications'))
);
export const EditProduct = Loadable(lazyWithReload(() => import('../pages/dashboard/EditProduct')));
export const ProductMembersArea = Loadable(
  lazyWithReload(() =>
    import('../sections/@dashboard/product-members-area').then((module) => ({
      default: module.ProductMembersArea,
    }))
  )
);
export const NotificationsPage = Loadable(
  lazyWithReload(() => import('../pages/dashboard/NotificationsPage'))
);
export const AffiliatesPage = Loadable(
  lazyWithReload(() => import('../pages/dashboard/AffiliatesPage'))
);
export const AffiliateInvitePage = Loadable(
  lazyWithReload(() => import('../pages/dashboard/AffiliateInvite'))
);
export const CouponsPage = Loadable(lazyWithReload(() => import('../pages/dashboard/CouponsPage')));
export const PartnersPage = Loadable(
  lazyWithReload(() => import('../pages/dashboard/PartnersPage'))
);
export const TeamPage = Loadable(lazyWithReload(() => import('../pages/dashboard/Team')));
export const BusinessTeamPage = Loadable(
  lazyWithReload(() => import('../pages/dashboard/BusinessTeam'))
);
export const BusinessCreatePage = Loadable(
  lazyWithReload(() => import('../pages/dashboard/BusinessCreate'))
);
export const AccountDetailsPage = Loadable(
  lazyWithReload(() => import('../pages/dashboard/AccountDetails'))
);
export const AnotherAccountsPage = Loadable(
  lazyWithReload(() => import('../pages/dashboard/AnotherAccounts'))
);
export const ProductExitPopupPage = LoadableContent(
  lazyWithReload(() => import('../pages/dashboard/ProductExitPopupPage'))
);

export const ProductPixelsPage = LoadableContent(
  lazyWithReload(() => import('../pages/dashboard/ProductPixelsPage'))
);
export const ProductAffiliatesPage = LoadableContent(
  lazyWithReload(() => import('../pages/dashboard/ProductAffiliatesPage'))
);
export const ProductCoProductionsPage = LoadableContent(
  lazyWithReload(() => import('../pages/dashboard/ProductCoProductionsPage'))
);

// DASHBOARD: PRODUCT RESUME SHELL
export const ProductResumeShell = Loadable(
  lazyWithReload(() => import('../sections/@dashboard/products/Resume/layout/ProductResumeShell'))
);
export const ProductEditInfoPage = LoadableContent(
  lazyWithReload(() =>
    import('../sections/@dashboard/products/ProductEdit/pages/ProductEditInfoPage')
  )
);
export const ProductEditPaymentModelPage = LoadableContent(
  lazyWithReload(() =>
    import('../sections/@dashboard/products/ProductEdit/pages/ProductEditPaymentModelPage')
  )
);
export const ProductEditSettingsPage = LoadableContent(
  lazyWithReload(() =>
    import('../sections/@dashboard/products/ProductEdit/pages/ProductEditSettingsPage')
  )
);
export const ResumeMembersAreaPage = LoadableContent(
  lazyWithReload(() =>
    import('../sections/@dashboard/products/Resume/Sections/ResumeMembersAreaPage')
  )
);
export const ResumeMembersV2Page = LoadableContent(
  lazyWithReload(() =>
    import('../sections/@dashboard/products/Resume/Sections/ResumeMembersV2Page')
  )
);
export const ResumeMembersV3Page = LoadableContent(
  lazyWithReload(() =>
    import('../sections/@dashboard/products/Resume/Sections/ResumeMembersV3Page')
  )
);
export const ResumeMembersExternalPage = LoadableContent(
  lazyWithReload(() =>
    import('../sections/@dashboard/products/Resume/Sections/ResumeMembersExternalPage')
  )
);
export const Offers = LoadableContent(lazyWithReload(() => import('../pages/dashboard/Offers')));
export const AddOffer = LoadableContent(
  lazyWithReload(() => import('../pages/dashboard/AddOffer'))
);
export const ProductUpsellResumePage = LoadableContent(
  lazyWithReload(() => import('../pages/dashboard/ProductUpsellResumePage'))
);
export const ProductCheckoutLinksPage = LoadableContent(
  lazyWithReload(() => import('../pages/dashboard/ProductCheckoutLinksPage'))
);
export const CaktoAnalyticsPage = LoadableContent(
  lazyWithReload(() => import('../pages/dashboard/CaktoAnalyticsPage'))
);
export const CaktoAnalyticsAccountPage = LoadableContent(
  lazyWithReload(() => import('../pages/dashboard/CaktoAnalyticsAccountPage'))
);
export const ProductWhatsappPage = LoadableContent(
  lazyWithReload(() => import('../pages/dashboard/ProductWhatsappPage'))
);
export const AffiliateProductSettingsPage = LoadableContent(
  lazyWithReload(() => import('../pages/dashboard/AffiliateProductSettingsPage'))
);
export const ProductCheckoutPageV2 = LoadableContent(
  lazyWithReload(() => import('../pages/dashboard/ProductCheckoutPageV2'))
);
export const OrderBumpsPageV2 = LoadableContent(
  lazyWithReload(() =>
    import('../sections/@dashboard/product-bumps').then((module) => ({
      default: module.OrderBumpsPageV2,
    }))
  )
);
export const ProductCouponsPageV2 = LoadableContent(
  lazyWithReload(() =>
    import('../sections/@dashboard/product-coupons').then((module) => ({
      default: module.ProductCouponsPageV2,
    }))
  )
);

export const AwardsPage = Loadable(lazyWithReload(() => import('../pages/dashboard/AwardsPage')));
// TEST RENDER PAGE BY ROLE
export const PermissionDeniedPage = Loadable(
  lazyWithReload(() => import('../pages/dashboard/PermissionDeniedPage'))
);
export const CheckoutBuilder = Loadable(
  lazyWithReload(() => import('../pages/checkout-builder/CheckoutBuilderPage'))
);

// BLANK PAGE
export const BlankPage = Loadable(lazyWithReload(() => import('../pages/dashboard/BlankPage')));

// MAIN
export const Page500 = Loadable(lazyWithReload(() => import('../pages/Page500')));
export const Page403 = Loadable(lazyWithReload(() => import('../pages/Page403')));
export const Page404 = Loadable(lazyWithReload(() => import('../pages/Page404')));
export const HomePage = Loadable(lazyWithReload(() => import('../pages/HomePage')));
export const AboutPage = Loadable(lazyWithReload(() => import('../pages/AboutPage')));
export const Contact = Loadable(lazyWithReload(() => import('../pages/ContactPage')));
export const PricingPage = Loadable(lazyWithReload(() => import('../pages/PricingPage')));
export const McpPage = Loadable(lazyWithReload(() => import('../pages/McpPage')));
export const PaymentPage = Loadable(lazyWithReload(() => import('../pages/PaymentPage')));
export const ComingSoonPage = Loadable(lazyWithReload(() => import('../pages/ComingSoonPage')));
export const MaintenancePage = Loadable(lazyWithReload(() => import('../pages/MaintenancePage')));

// DEMO COMPONENTS
// ----------------------------------------------------------------------

export const ComponentsOverviewPage = Loadable(
  lazyWithReload(() => import('../pages/components/ComponentsOverviewPage'))
);

// FOUNDATION
export const FoundationColorsPage = Loadable(
  lazyWithReload(() => import('../pages/components/foundation/FoundationColorsPage'))
);
export const FoundationTypographyPage = Loadable(
  lazyWithReload(() => import('../pages/components/foundation/FoundationTypographyPage'))
);
export const FoundationShadowsPage = Loadable(
  lazyWithReload(() => import('../pages/components/foundation/FoundationShadowsPage'))
);
export const FoundationGridPage = Loadable(
  lazyWithReload(() => import('../pages/components/foundation/FoundationGridPage'))
);
export const FoundationIconsPage = Loadable(
  lazyWithReload(() => import('../pages/components/foundation/FoundationIconsPage'))
);

// MUI COMPONENTS
export const MUIAccordionPage = Loadable(
  lazyWithReload(() => import('../pages/components/mui/MUIAccordionPage'))
);
export const MUIAlertPage = Loadable(
  lazyWithReload(() => import('../pages/components/mui/MUIAlertPage'))
);
export const MUIAutocompletePage = Loadable(
  lazyWithReload(() => import('../pages/components/mui/MUIAutocompletePage'))
);
export const MUIAvatarPage = Loadable(
  lazyWithReload(() => import('../pages/components/mui/MUIAvatarPage'))
);
export const MUIBadgePage = Loadable(
  lazyWithReload(() => import('../pages/components/mui/MUIBadgePage'))
);
export const MUIBreadcrumbsPage = Loadable(
  lazyWithReload(() => import('../pages/components/mui/MUIBreadcrumbsPage'))
);
export const MUIButtonsPage = Loadable(
  lazyWithReload(() => import('../pages/components/mui/MUIButtonsPage'))
);
export const MUICheckboxPage = Loadable(
  lazyWithReload(() => import('../pages/components/mui/MUICheckboxPage'))
);
export const MUIChipPage = Loadable(
  lazyWithReload(() => import('../pages/components/mui/MUIChipPage'))
);
export const MUIDataGridPage = Loadable(
  lazyWithReload(() => import('../pages/components/mui/MUIDataGridPage'))
);
export const MUIDialogPage = Loadable(
  lazyWithReload(() => import('../pages/components/mui/MUIDialogPage'))
);
export const MUIListPage = Loadable(
  lazyWithReload(() => import('../pages/components/mui/MUIListPage'))
);
export const MUIMenuPage = Loadable(
  lazyWithReload(() => import('../pages/components/mui/MUIMenuPage'))
);
export const MUIPaginationPage = Loadable(
  lazyWithReload(() => import('../pages/components/mui/MUIPaginationPage'))
);
export const MUIPickersPage = Loadable(
  lazyWithReload(() => import('../pages/components/mui/MUIPickersPage'))
);
export const MUIPopoverPage = Loadable(
  lazyWithReload(() => import('../pages/components/mui/MUIPopoverPage'))
);
export const MUIProgressPage = Loadable(
  lazyWithReload(() => import('../pages/components/mui/MUIProgressPage'))
);
export const MUIRadioButtonsPage = Loadable(
  lazyWithReload(() => import('../pages/components/mui/MUIRadioButtonsPage'))
);
export const MUIRatingPage = Loadable(
  lazyWithReload(() => import('../pages/components/mui/MUIRatingPage'))
);
export const MUISliderPage = Loadable(
  lazyWithReload(() => import('../pages/components/mui/MUISliderPage'))
);
export const MUIStepperPage = Loadable(
  lazyWithReload(() => import('../pages/components/mui/MUIStepperPage'))
);
export const MUISwitchPage = Loadable(
  lazyWithReload(() => import('../pages/components/mui/MUISwitchPage'))
);
export const MUITablePage = Loadable(
  lazyWithReload(() => import('../pages/components/mui/MUITablePage'))
);
export const MUITabsPage = Loadable(
  lazyWithReload(() => import('../pages/components/mui/MUITabsPage'))
);
export const MUITextFieldPage = Loadable(
  lazyWithReload(() => import('../pages/components/mui/MUITextFieldPage'))
);
export const MUITimelinePage = Loadable(
  lazyWithReload(() => import('../pages/components/mui/MUITimelinePage'))
);
export const MUITooltipPage = Loadable(
  lazyWithReload(() => import('../pages/components/mui/MUITooltipPage'))
);
export const MUITransferListPage = Loadable(
  lazyWithReload(() => import('../pages/components/mui/MUITransferListPage'))
);
export const MUITreesViewPage = Loadable(
  lazyWithReload(() => import('../pages/components/mui/MUITreesViewPage'))
);

// EXTRA
export const DemoAnimatePage = Loadable(
  lazyWithReload(() => import('../pages/components/extra/DemoAnimatePage'))
);
export const DemoCarouselsPage = Loadable(
  lazyWithReload(() => import('../pages/components/extra/DemoCarouselsPage'))
);
export const DemoChartsPage = Loadable(
  lazyWithReload(() => import('../pages/components/extra/DemoChartsPage'))
);
export const DemoCopyToClipboardPage = Loadable(
  lazyWithReload(() => import('../pages/components/extra/DemoCopyToClipboardPage'))
);
export const DemoEditorPage = Loadable(
  lazyWithReload(() => import('../pages/components/extra/DemoEditorPage'))
);
export const DemoFormValidationPage = Loadable(
  lazyWithReload(() => import('../pages/components/extra/DemoFormValidationPage'))
);
export const DemoImagePage = Loadable(
  lazyWithReload(() => import('../pages/components/extra/DemoImagePage'))
);
export const DemoLabelPage = Loadable(
  lazyWithReload(() => import('../pages/components/extra/DemoLabelPage'))
);
export const DemoLightboxPage = Loadable(
  lazyWithReload(() => import('../pages/components/extra/DemoLightboxPage'))
);
export const DemoMapPage = Loadable(
  lazyWithReload(() => import('../pages/components/extra/DemoMapPage'))
);
export const DemoMegaMenuPage = Loadable(
  lazyWithReload(() => import('../pages/components/extra/DemoMegaMenuPage'))
);
export const DemoMultiLanguagePage = Loadable(
  lazyWithReload(() => import('../pages/components/extra/DemoMultiLanguagePage'))
);
export const DemoNavigationBarPage = Loadable(
  lazyWithReload(() => import('../pages/components/extra/DemoNavigationBarPage'))
);
export const DemoOrganizationalChartPage = Loadable(
  lazyWithReload(() => import('../pages/components/extra/DemoOrganizationalChartPage'))
);
export const DemoScrollbarPage = Loadable(
  lazyWithReload(() => import('../pages/components/extra/DemoScrollbarPage'))
);
export const DemoSnackbarPage = Loadable(
  lazyWithReload(() => import('../pages/components/extra/DemoSnackbarPage'))
);
export const DemoTextMaxLinePage = Loadable(
  lazyWithReload(() => import('../pages/components/extra/DemoTextMaxLinePage'))
);
export const DemoUploadPage = Loadable(
  lazyWithReload(() => import('../pages/components/extra/DemoUploadPage'))
);
export const DemoMarkdownPage = Loadable(
  lazyWithReload(() => import('../pages/components/extra/DemoMarkdownPage'))
);
export const SubscriptionsTable = Loadable(
  lazyWithReload(() => import('../pages/subscription/SubscriptionPage'))
);
// REFUND
export const RefundHome = Loadable(lazyWithReload(() => import('../pages/refund/Home')));
export const RefundPurchases = Loadable(lazyWithReload(() => import('../pages/refund/Purchases')));
export const RefundPurchase = Loadable(lazyWithReload(() => import('../pages/refund/Purchase')));

export const SubscriberHome = LoadablePublic(
  lazyWithReload(() => import('../pages/subscriber/Home'))
);
export const SubscriberSubscriptions = LoadablePublic(
  lazyWithReload(() => import('../pages/subscriber/Subscriptions'))
);
export const SubscriberPayment = LoadablePublic(
  lazyWithReload(() => import('../pages/subscriber/Payment'))
);
export const SubscriberPaymentSuccess = LoadablePublic(
  lazyWithReload(() => import('../pages/subscriber/PaymentSuccess'))
);
export const SubscriberHistory = LoadablePublic(
  lazyWithReload(() => import('../pages/subscriber/History'))
);

// RATING SURVEY (Public - CAK-2)
export const RatingSurveyPage = Loadable(
  lazyWithReload(() => import('../pages/@public/RatingSurveyPage'))
);

// MEMBERS
export const MembersDiscordFinishConfigPage = Loadable(
  lazyWithReload(() =>
    import('../pages/members/discord/finish-config/MembersDiscordFinishConfigPage')
  )
);

// MARKETPLACE
export const MarketplaceLayout = LoadablePublic(lazyWithReload(() => import('../layouts/marketplace/MarketplaceLayout')));
export const MarketplaceHomePage = LoadablePublic(lazyWithReload(() => import('../pages/marketplace/MarketplaceHomePage')));
export const MarketplaceSearchPage = LoadablePublic(lazyWithReload(() => import('../pages/marketplace/MarketplaceSearchPage')));
export const MarketplaceProductPage = LoadablePublic(lazyWithReload(() => import('../pages/marketplace/MarketplaceProductPage')));
export const MarketplaceSellerPage = LoadablePublic(lazyWithReload(() => import('../pages/marketplace/MarketplaceSellerPage')));
export const MarketplaceFavoritesPage = LoadablePublic(lazyWithReload(() => import('../pages/marketplace/MarketplaceFavoritesPage')));
